import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnType, TablePaginationConfig } from 'antd/lib/table/interface';

import { IngestionArtifactType, IngestionResourceModel } from '../../../../../../gamma-api';
import { EMPTY_VALUE, formatDateTimeShort } from '../../../../../utils/stringUtil';
import { formatFileSize } from '../../../../../utils/fileUtil';
import IbIcon from '../../../../../components/common/IbIcon';
import IbButton from '../../../../../components/common/IbButton';
import IbContextMenu from '../../../../../components/common/IbContextMenu';
import IbTable from '../../../../../components/common/IbTable';
import { IFileTableItem } from '../FileTable';
import ResourceAction from '../ResourceAction';
import ResourceVolume from '../ResourceVolume';
import ResourceStatus from '../ResourceStatus';
import { TEXT_HTML_MIME } from '../../const';
import IbTextHighlighter from '../../../../../components/common/IbTextHighlighter';
import { RESOURCES_PAGE_SIZE } from '../../../../../constants';
import IbPagination from '../../../../../components/common/IbPagination';

import './index.less';

import { MAIN_CLASS_NAME, PAGINATION_ROW_CLASS_NAME } from './const';

const ACTION_COLUMN_FIXED_WIDTH = '105px';
const SIZE_COLUMN_FIXED_WIDTH = '130px';
const VOLUME_COLUMN_FIXED_WIDTH = '130px';
const STATUS_COLUMN_FIXED_WIDTH = '185px';
const DATE_COLUMN_FIXED_WIDTH = '155px';
const CONTEXT_COLUMN_FIXED_WIDTH = 40;

export enum ResourceTableField {
  Action = 'Action',
  Name = 'Name',
  Url = 'Url',
  Size = 'Size',
  Volume = 'Volume',
  Status = 'Status',
  Date = 'Date',
  Context = 'Context',
}

export interface IResourceTableItem {
  file?: IFileTableItem;
  resource?: IngestionResourceModel;
}

export interface IResourceTableProps {
  sourceLoading: boolean;
  pageLoading: boolean;
  items: IResourceTableItem[];
  fields: ResourceTableField[];
  searchText?: string;
  pagination: TablePaginationConfig;
  onPaginationChange?: (page: number) => void;
  onOpen?: (resource: IngestionResourceModel, artifactType: IngestionArtifactType) => void;
}

const ResourceTable: React.FC<IResourceTableProps> = ({
  sourceLoading,
  pageLoading,
  items,
  fields,
  searchText,
  pagination,
  onPaginationChange,
  onOpen,
}) => {
  const { t, i18n } = useTranslation();

  const [selectedResourceId, setSelectedResourceId] = useState<string>();

  const onUrlClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
  };

  const onStatusClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
  };

  const onRowClick = (item: IResourceTableItem, column: ColumnType<unknown>) => {
    if (column.title && item.resource) {
      onOpen?.(item.resource, IngestionArtifactType.Article);
    }
  };

  const getRowKey = (item: IResourceTableItem): string => {
    return item.resource?.resource_id || item.file?.server?.file_id || item.file?.client?.uid || EMPTY_VALUE;
  };

  const getDisplayName = (item: IResourceTableItem): string => {
    return (
      item.resource?.identities.latest?.title ||
      item.resource?.identities.latest?.name ||
      item.file?.server?.name ||
      item.file?.client?.name ||
      EMPTY_VALUE
    );
  };

  const getContentUrl = (item: IResourceTableItem): string => {
    return (
      item.resource?.references.latest?.artifact_urls[0]?.public ||
      item.resource?.references.latest?.file_urls[0]?.public ||
      item.file?.server?.urls.public ||
      ''
    );
  };

  const getDisplayUrl = (item: IResourceTableItem): string => {
    return item.resource?.url || item.file?.server?.urls.public || '';
  };

  const getContentSize = (item: IResourceTableItem): number => {
    return item.resource?.volumes.latest?.content_size || item.file?.server?.size || item.file?.client?.size || 0;
  };

  const renderAction = (item: IResourceTableItem) => {
    return <ResourceAction action={item.resource?.action} />;
  };

  const renderName = (item: IResourceTableItem) => {
    const name = getDisplayName(item);
    const url = getContentUrl(item);

    if (!url) {
      return <IbTextHighlighter highlightedText={searchText} text={name} />;
    }

    return (
      <a href={url} rel="noreferrer" target="_blank" onClick={onUrlClick}>
        <IbTextHighlighter highlightedText={searchText} text={name} />
      </a>
    );
  };

  const renderUrl = (item: IResourceTableItem) => {
    const url = getDisplayUrl(item);

    if (!url) {
      return <span>{EMPTY_VALUE}</span>;
    }

    return (
      <a href={url} rel="noreferrer" target="_blank" onClick={onUrlClick}>
        <IbTextHighlighter highlightedText={searchText} text={url} />
      </a>
    );
  };

  const renderSize = (item: IResourceTableItem) => {
    const size = getContentSize(item);
    return formatFileSize(size);
  };

  const renderVolume = (item: IResourceTableItem) => {
    return <ResourceVolume value={item.resource?.volumes.latest} />;
  };

  const renderStatus = (item: IResourceTableItem) => {
    return (
      <ResourceStatus
        action={item?.resource?.action}
        direction="vertical"
        loading={false}
        problem={item?.resource?.problem}
        rolling={false}
        stage={item?.resource?.stage}
        status={item?.resource?.status}
        onClick={onStatusClick}
      />
    );
  };

  const renderDate = (item: IResourceTableItem) => {
    return formatDateTimeShort(item.resource?.ingested_on, i18n.language);
  };

  const renderContext = (item: IResourceTableItem) => {
    const url = getContentUrl(item);

    const onButtonClick = () => {
      item.resource && setSelectedResourceId(item.resource.resource_id);
    };

    const onMenuClose = () => {
      setSelectedResourceId(undefined);
    };

    const onDownloadSelect = () => {
      // eslint-disable-next-line security/detect-non-literal-fs-filename
      url && window.open(url, '_blank');
    };

    const onArticlesSelect = () => {
      item.resource && onOpen?.(item.resource, IngestionArtifactType.Article);
    };

    const onFragmentsSelect = () => {
      item.resource && onOpen?.(item.resource, IngestionArtifactType.Fragment);
    };

    const onDocumentsSelect = () => {
      item.resource && onOpen?.(item.resource, IngestionArtifactType.Document);
    };

    const menuItems = [
      {
        icon: <IbIcon iconName="download" size={20} />,
        text: t('Resource table: Context: Download'),
        onSelect: onDownloadSelect,
        disabled: !url,
      },
      {
        icon: <IbIcon iconName="file-text" size={20} />,
        text: t('Resource table: Context: Open articles'),
        onSelect: onArticlesSelect,
        disabled: !item.resource,
      },
      {
        icon: <IbIcon iconName="view-list" size={20} />,
        text: t('Resource table: Context: Open fragments'),
        onSelect: onFragmentsSelect,
        disabled: !item.resource,
      },
      {
        icon: <IbIcon iconName="web-page" size={20} />,
        text: t('Resource table: Context: Open documents'),
        onSelect: onDocumentsSelect,
        disabled: !item.resource || item.resource.identities.latest?.mime !== TEXT_HTML_MIME,
      },
    ];

    return (
      <IbContextMenu
        menuItems={menuItems}
        visible={item.resource && item.resource.resource_id === selectedResourceId}
        onClose={onMenuClose}
      >
        <IbButton icon={<IbIcon iconName="more-one" />} type="icon" onClick={onButtonClick} />
      </IbContextMenu>
    );
  };

  const columns: ColumnType<IResourceTableItem>[] = [];

  const hasAction = fields.includes(ResourceTableField.Action);
  const hasName = fields.includes(ResourceTableField.Name);
  const hasUrl = fields.includes(ResourceTableField.Url);
  const hasSize = fields.includes(ResourceTableField.Size);
  const hasStatus = fields.includes(ResourceTableField.Status);

  const addActionColumn = (width?: string) => {
    columns.push({
      key: 'action',
      title: t('Resource table: Action'),
      render: renderAction,
      width,
    });
  };

  const addNameColumn = (width?: string) => {
    columns.push({
      key: 'name',
      title: t('Resource table: Name'),
      render: renderName,
      width,
      ellipsis: true,
    });
  };

  const addUrlColumn = (width?: string) => {
    columns.push({
      key: 'url',
      title: t('Resource table: URL'),
      render: renderUrl,
      width,
      ellipsis: true,
    });
  };

  const addSizeColumn = (width?: string) => {
    columns.push({
      key: 'size',
      title: t('Resource table: Size'),
      render: renderSize,
      width,
    });
  };

  const addVolumeColumn = (width?: string) => {
    columns.push({
      key: 'volume',
      title: t('Resource table: Volume'),
      render: renderVolume,
      width,
    });
  };

  const addStatusColumn = (width?: string) => {
    columns.push({
      key: 'status',
      title: t('Resource table: Status'),
      render: renderStatus,
      width,
    });
  };

  const addDateColumn = (width?: string) => {
    columns.push({
      key: 'context',
      title: t('Resource table: Date'),
      render: renderDate,
      width,
    });
  };

  const addContextColumn = () => {
    columns.push({
      title: '',
      render: renderContext,
      width: CONTEXT_COLUMN_FIXED_WIDTH,
    });
  };

  if (fields.length === 2 && hasName && hasUrl) {
    addNameColumn('40%');
    addUrlColumn('60%');
  } else if (fields.length === 3 && hasAction && hasName && hasUrl) {
    addActionColumn(ACTION_COLUMN_FIXED_WIDTH);
    addNameColumn('50%');
    addUrlColumn('50%');
  } else if (fields.length === 3 && hasName && hasUrl && hasStatus) {
    addNameColumn('50%');
    addUrlColumn('50%');
    addStatusColumn(STATUS_COLUMN_FIXED_WIDTH);
  } else if (fields.length === 3 && hasName && hasSize && hasStatus) {
    addNameColumn();
    addSizeColumn(SIZE_COLUMN_FIXED_WIDTH);
    addStatusColumn(STATUS_COLUMN_FIXED_WIDTH);
  } else {
    for (const field of fields) {
      if (field === ResourceTableField.Name) {
        addNameColumn();
      } else if (field === ResourceTableField.Url) {
        addUrlColumn();
      } else if (field === ResourceTableField.Size) {
        addSizeColumn(SIZE_COLUMN_FIXED_WIDTH);
      } else if (field === ResourceTableField.Volume) {
        addVolumeColumn(VOLUME_COLUMN_FIXED_WIDTH);
      } else if (field === ResourceTableField.Status) {
        addStatusColumn(STATUS_COLUMN_FIXED_WIDTH);
      } else if (field === ResourceTableField.Date) {
        addDateColumn(DATE_COLUMN_FIXED_WIDTH);
      } else if (field === ResourceTableField.Context) {
        addContextColumn();
      }
    }
  }

  return (
    <>
      <IbTable
        className={MAIN_CLASS_NAME}
        columns={columns}
        dataSource={items}
        loading={sourceLoading || pageLoading}
        rowKey={getRowKey}
        size="middle"
        onRowClick={onRowClick}
      />
      {!sourceLoading && (pagination.total ?? 0) > RESOURCES_PAGE_SIZE && (
        <div className={PAGINATION_ROW_CLASS_NAME}>
          <IbPagination
            current={pagination.current}
            defaultCurrent={1}
            defaultPageSize={RESOURCES_PAGE_SIZE}
            pageSize={RESOURCES_PAGE_SIZE}
            showSizeChanger={false}
            total={pagination.total}
            onChange={onPaginationChange}
          />
        </div>
      )}
    </>
  );
};

export default ResourceTable;
